import React, { useRef, useEffect } from "react";
import InfoPopup from "../InfoPopup";
import styles from "./queryBreakdownModal.module.css";
import {
  CHART_COLORS,
  IX_COLOR,
  hexToRgba,
  formatTick,
  getYAxis,
} from "../audienceChartUtils";
import { useBarHoverTooltip } from "../../hooks/use-bar-hover-tooltip";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";

interface FilterCondition {
  operator: string;
  value: string;
}

interface FilterApplied {
  attribute: string;
  conditions: FilterCondition[];
  status: string;
}

export interface ExecutedQuery {
  subquery_id: string;
  contributor_bucket_name: string;
  contributor_organization_name: string;
  audience_result_count: number;
  differential_privacy_query?: { type: string; result_count: number };
  contributor_record_count?: number;
  filters_applied?: FilterApplied[];
}

// Per-contributor DP subqueries only run for intersection queries; a
// single-bucket query's contributor row never gets one (type stays "UNSET"),
// so fall back to the flat load-phase count in that case.
export const dpAwareCount = (q: ExecutedQuery) =>
  q.differential_privacy_query?.type === "DP"
    ? q.differential_privacy_query.result_count
    : q.audience_result_count || 0;

export interface QueryBreakdownData {
  queryName: string;
  queryId: string;
  campaignName?: string;
  intersectionCount: number;
  executedQueries: ExecutedQuery[];
  filterStr?: string[];
  attributesSelected?: string[];
  nlQuery?: boolean;
  nlQueryText?: string;
}

interface QueryBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: QueryBreakdownData;
}

const CHART_H = 220;

const QueryBreakdownModal: React.FC<QueryBreakdownModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const {
    queryName,
    queryId,
    campaignName,
    intersectionCount,
    executedQueries,
    filterStr = [],
    nlQuery = false,
    nlQueryText,
  } = data;

  const datasetCount = executedQueries.length;

  const chartBodyRef = useRef<HTMLDivElement>(null);
  const {
    hoveredIdx,
    tooltipColor,
    tooltipStyle,
    tooltipFlipped,
    tooltipRef,
    showTooltip,
    hideTooltip,
    reset: resetTooltip,
  } = useBarHoverTooltip(chartBodyRef);

  const normalizeOperator = (op: string): string => {
    if (op === "eq") return "=";
    if (op === "neq" || op === "ne") return "≠";
    if (op === "gt") return ">";
    if (op === "gte" || op === "ge") return "≥";
    if (op === "lt") return "<";
    if (op === "lte" || op === "le") return "≤";
    return op;
  };

  const hasFiltersApplied = executedQueries.some((q) =>
    q.filters_applied?.some((f) => f.status === "applied"),
  );

  const filterChips: string[] = hasFiltersApplied
    ? Array.from(
        new Set(
          executedQueries.flatMap((q) =>
            (q.filters_applied || [])
              .filter((f) => f.status === "applied")
              .flatMap((f) =>
                f.conditions
                  .filter((c) => c.operator !== "logical")
                  .map(
                    (c) =>
                      `${f.attribute} ${normalizeOperator(c.operator)} ${c.value}`,
                  ),
              ),
          ),
        ),
      )
    : Array.from(
        new Map(
          filterStr
            .filter((s) => s.trim() !== "")
            .map((s) => [s.toLowerCase(), s]),
        ).values(),
      );

  const maxCount = Math.max(
    ...executedQueries.map((q) => dpAwareCount(q)),
    intersectionCount,
    1,
  );
  const { yMax, ticks } = getYAxis(maxCount * 1.15);

  const barHeightNum = (count: number) =>
    Math.max(2, (count / yMax) * CHART_H);
  const tickTopPx = (val: number) =>
    `${((1 - val / yMax) * CHART_H).toFixed(1)}px`;

  // Clear hover state when modal closes so tooltip doesn't ghost on next open
  useEffect(() => {
    if (!isOpen) resetTooltip();
  }, [isOpen, resetTooltip]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleBarEnter = (
    e: React.MouseEvent<HTMLDivElement>,
    idx: number,
  ) => {
    const color =
      idx < datasetCount ? CHART_COLORS[idx % CHART_COLORS.length] : IX_COLOR;
    showTooltip(e.currentTarget, idx, color);
  };

  const handleBarLeave = () => hideTooltip();

  // ── Tooltip content ───────────────────────────────────────────────────────

  const renderTooltipContent = () => {
    if (hoveredIdx === null) return null;
    const isIx = hoveredIdx === datasetCount;

    if (isIx) {
      return (
        <>
          <div className={styles.ttOrg}>Audience Size</div>
          <div className={styles.ttDs}>matched across all data partners</div>
          <div className={styles.ttCount}>
            {intersectionCount.toLocaleString("en-US")}
            <span className={styles.ttOf}>matched</span>
          </div>
        </>
      );
    }

    const ds = executedQueries[hoveredIdx];
    const matched = dpAwareCount(ds);

    const appliedFilters = (ds.filters_applied || []).filter(
      (f) => f.status === "applied",
    );
    const hasFiltersData = (ds.filters_applied || []).length > 0;
    const chips: string[] =
      appliedFilters.length > 0
        ? appliedFilters.flatMap((f) =>
            f.conditions
              .filter((c) => c.operator !== "logical")
              .map(
                (c) =>
                  `${f.attribute} ${normalizeOperator(c.operator)} ${c.value}`,
              ),
          )
        : hasFiltersData
          ? []
          : filterChips;

    return (
      <>
        <div className={styles.ttOrg}>{ds.contributor_organization_name}</div>
        <div className={styles.ttDs}>{ds.contributor_bucket_name}</div>
        <div className={styles.ttCount}>
          {matched.toLocaleString("en-US")}
          <span className={styles.ttOf}>matched</span>
        </div>
        {chips.length > 0 && (
          <>
            <div className={styles.ttDivider} />
            <div className={styles.ttChipsLabel}>Applied filters</div>
            <div className={styles.ttChips}>
              {chips.map((chip, i) => (
                <span key={i} className={styles.ttChip}>
                  <span className={styles.ttChipCheck}>{(FEATURES.curationSkin ? <OI n="check" size="1em" /> : "✓")}</span>
                  {chip}
                </span>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  // ── Chart ─────────────────────────────────────────────────────────────────

  const renderChart = () => {
    if (datasetCount === 0) {
      return (
        <div className={styles.emptyState}>
          <span className={styles.emptyStateIcon}>∅</span>
          <span className={styles.emptyStateMsg}>No contributor breakdown available</span>
          <span className={styles.emptyStateSub}>Run the query to generate data</span>
        </div>
      );
    }

    return (
      <div className={styles.chartFrame}>
        {/* Tooltip — opacity-based so CSS transition applies */}
        <div
          ref={tooltipRef}
          role="tooltip"
          data-testid="query-breakdown-chart-tooltip"
          className={`${styles.chartTooltip}${tooltipFlipped ? ` ${styles.chartTooltipFlipped}` : ""}`}
          style={{
            ...tooltipStyle,
            opacity: hoveredIdx !== null ? 1 : 0,
            borderLeft: `4px solid ${tooltipColor}`,
          }}
        >
          {renderTooltipContent()}
        </div>

        {/* Y-axis */}
        <div className={styles.yCol} aria-hidden="true">
          {[...ticks].reverse().map((tick) => (
            <span
              key={tick}
              className={styles.yLabel}
              style={{ top: tickTopPx(tick) }}
            >
              {formatTick(tick)}
            </span>
          ))}
        </div>

        {/* Chart body */}
        <div className={styles.chartBody} ref={chartBodyRef}>
          <div className={styles.barsArea}>
            {/* Grid lines */}
            {ticks.map((tick) => (
              <div
                key={tick}
                className={`${styles.gl}${tick === ticks[ticks.length - 1] ? ` ${styles.glCap}` : ""}`}
                style={{ top: tickTopPx(tick) }}
              />
            ))}

            {/* Bars — keyed to queryId so animations replay when a new query loads */}
            <div className={styles.barsFlex} key={queryId}>
              {executedQueries.map((ds, i) => {
                const count = dpAwareCount(ds);
                const color = CHART_COLORS[i % CHART_COLORS.length];
                const hNum = barHeightNum(count);
                const isDimmed = hoveredIdx !== null && hoveredIdx !== i;

                // Capacity background — shows total dataset size when available
                const capacityH = ds.contributor_record_count
                  ? Math.min(
                      CHART_H,
                      Math.max(2, (ds.contributor_record_count / yMax) * CHART_H),
                    )
                  : null;

                return (
                  <div key={i} className={styles.barCol}>
                    {/* Lighter capacity bar behind the matched bar */}
                    {capacityH !== null && (
                      <div
                        className={styles.barCapacity}
                        style={{
                          height: `${capacityH}px`,
                          background: hexToRgba(color, 0.14),
                        }}
                      />
                    )}
                    <div
                      className={styles.bar}
                      style={
                        {
                          height: `${hNum}px`,
                          "--d": `${60 + i * 70}ms`,
                          background: color,
                          opacity: isDimmed ? 0.22 : 1,
                        } as React.CSSProperties
                      }
                      onMouseEnter={(e) => handleBarEnter(e, i)}
                      onMouseLeave={handleBarLeave}
                    >
                      <span
                        className={styles.barVal}
                        style={{
                          color,
                          opacity: isDimmed ? 0 : 1,
                          transition: "opacity 0.2s",
                        }}
                      >
                        {count.toLocaleString("en-US")}
                      </span>
                    </div>
                  </div>
                );
              })}

              {datasetCount > 1 && (
                <>
                  <div className={styles.ixDivider} aria-hidden="true" />

                  {/* Intersection bar — or zero-state guard */}
                  <div className={styles.barCol}>
                    {intersectionCount === 0 ? (
                      <div className={styles.zeroIxSlot}>
                        <span className={styles.zeroIxIcon}>∅</span>
                        <span className={styles.zeroIxText}>No intersection</span>
                      </div>
                    ) : (
                      <div
                        className={`${styles.bar} ${styles.iBar}`}
                        style={
                          {
                            height: `${barHeightNum(intersectionCount)}px`,
                            "--d": `${60 + datasetCount * 70 + 70}ms`,
                            opacity:
                              hoveredIdx !== null && hoveredIdx !== datasetCount
                                ? 0.22
                                : 1,
                          } as React.CSSProperties
                        }
                        onMouseEnter={(e) => handleBarEnter(e, datasetCount)}
                        onMouseLeave={handleBarLeave}
                      >
                        <span
                          className={styles.barVal}
                          style={{
                            opacity:
                              hoveredIdx !== null && hoveredIdx !== datasetCount
                                ? 0
                                : 1,
                            transition: "opacity 0.2s",
                          }}
                        >
                          {intersectionCount.toLocaleString("en-US")}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* X-axis labels */}
          <div className={styles.xRow} aria-hidden="true" role="presentation">
            {executedQueries.map((ds, i) => (
              <div key={i} className={styles.xItem}>
                <span
                  className={styles.xOrg}
                  style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}
                  title={ds.contributor_organization_name}
                >
                  {ds.contributor_organization_name}
                </span>
                <span className={styles.xDs} title={ds.contributor_bucket_name}>
                  {ds.contributor_bucket_name}
                </span>
              </div>
            ))}
            {datasetCount > 1 && (
              <>
                <div className={styles.xIxGap} />
                <div className={`${styles.xItem} ${styles.xItemIx}`}>
                  <span className={styles.xOrg}>Audience</span>
                  <span className={styles.xDs}>all data partners</span>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    );
  };

  return (
    <InfoPopup
      isOpen={isOpen}
      onClose={onClose}
      headerText="Audience Breakdown"
      contentWidth={700}
    >
      {/* Campaign name + summary strip */}
      {(campaignName || queryName) && (
        <div className={styles.querySubHeader}>
          <div className={styles.querySubRow}>
            <span className={styles.querySubKey}>Cohort:</span>
            <span className={styles.querySubName}>{campaignName || queryName}</span>
          </div>
        </div>
      )}

      <div className={styles.summaryStrip}>
        <div className={styles.summaryMetric}>
          <span className={styles.summaryValue}>
            {intersectionCount.toLocaleString("en-US")}
          </span>
          <span className={styles.summaryLabel}>Audience Size</span>
        </div>
        <div className={styles.summaryDivider} />
        <div className={styles.summaryMetric}>
          <span className={styles.summaryValue}>{datasetCount}</span>
          <span className={styles.summaryLabel}>Data Partners</span>
        </div>
      </div>

      {/* Contributor breakdown section */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <span className={styles.sectionTitleText}>Contributor Breakdown</span>
          <span className={styles.sectionTitleLine} />
          <span className={styles.ixBadge}>
            {datasetCount > 1 && <span className={styles.ixBadgeSym}>∩</span>}
            {intersectionCount.toLocaleString("en-US")}{" "}
            {datasetCount === 1 ? "Matched" : "Audience Matched"}
          </span>
        </div>
        {renderChart()}
      </div>

      {/* AI-generated query section */}
      {nlQuery && nlQueryText && (
        <div className={styles.nlSection}>
          <span className={styles.nlIcon}>✦</span>
          <div className={styles.nlContent}>
            <div className={styles.nlLabel}>AI-generated query</div>
            <div className={styles.nlPromptRow}>
              <span className={styles.nlPromptLabel}>Prompt</span>
              <span className={styles.nlText}>"{nlQueryText}"</span>
            </div>
          </div>
        </div>
      )}
    </InfoPopup>
  );
};

export default QueryBreakdownModal;
